// Define FeatureValueType locally to avoid circular dependency
export enum FeatureValueType {
  BOOLEAN = 'boolean',
  NUMBER = 'number',
  STRING = 'string',
  JSON = 'json',
}

// Feature keys used in subscription plans - MVP version
export const FEATURE_KEYS = {
  // Limits
  MAX_USERS: 'max_users',
  MAX_PROJECTS: 'max_projects',
  MAX_CUSTOM_FIELDS: 'max_custom_fields',
  MAX_TEMPLATES: 'max_templates',
  MAX_FORMS: 'max_forms',
  MAX_REPORTS: 'max_reports',
  MAX_DASHBOARDS: 'max_dashboards',
  MAX_COLLECTIONS: 'max_collections',
  MAX_DEALS: 'max_deals',
  MAX_CONTACTS: 'max_contacts',
  MAX_ENTITIES: 'max_entities',
  STORAGE_GB: 'storage_gb',
  MARKETING_HOURLY_SENDS: 'marketing_hourly_sends',
  MARKETING_DAILY_SENDS: 'marketing_daily_sends',

  // AI Features
  AI_CREDITS_MONTHLY: 'ai_credits_monthly',

  // Core Features
  PROJECTS: 'projects',
  TASKS_UNLIMITED: 'tasks_unlimited',
  TICKETS: 'tickets',
  TIME_TRACKING: 'time_tracking',
  DISCUSSIONS: 'discussions',
  FORMS: 'forms',
  SPRINTS_CYCLES: 'sprints_cycles',
  FOCUS_MODE: 'focus_mode',
  NOTES_DOCUMENTS: 'notes_documents',
  CALENDAR_PLANNER: 'calendar_planner',
  PERSONAL_CALENDAR_EMAIL_SYNC: 'personal_calendar_email_sync',
  TIMESHEETS: 'timesheets',
  CONTACTS: 'contacts',
  CUSTOM_FIELDS: 'custom_fields',
  IMPORT_WIZARD: 'import_wizard',
  DESKTOP_MOBILE_APPS: 'desktop_mobile_apps',

  // Entity Management
  ENTITY_PORTAL: 'client_portal',
  DEALS: 'deals',
  CLIENTS: 'clients',
  PORTAL_PERMISSION_TEMPLATES: 'portal_permission_templates',
  CLIENT_HEALTH: 'client_health',

  // Business Features
  EVENTS: 'events',
  DIRECTORY: 'directory',
  TEAM: 'team',
  INVENTORY: 'inventory',
  SUPPLIERS: 'suppliers',
  CONTRACTS: 'contracts',
  QUOTES: 'quotes',
  PRODUCTS: 'products',
  INVOICES: 'invoices',
  ONLINE_PAYMENTS: 'online_payments',
  BILLING_SCHEDULES: 'billing_schedules',
  CONTRACTOR_INVOICES: 'contractor_invoices',
  EXPENSES: 'expenses',
  BUDGETS: 'budgets',
  RATE_CARDS: 'rate_cards',
  PROFITABILITY: 'profitability',
  RESOURCE_PLANNING: 'resource_planning',
  SCHEDULING: 'scheduling',
  MEETING_RECORDER: 'meeting_recorder',
  KNOWLEDGE_BASE: 'knowledge_base',

  // Advanced Features
  ADVANCED_REPORTS: 'advanced_reports',
  API_ACCESS: 'api_access',
  WORKFLOWS: 'workflows',
  ANALYTICS: 'analytics',
  COLLECTIONS: 'collections',
  INTEGRATIONS: 'integrations',
  DATA_EXPORT: 'data_export',
  REMOVE_WATERMARK: 'remove_watermark',
  FORM_CAPTCHA: 'form_captcha',
  FORM_KIOSK_MODE: 'form_kiosk_mode',
  QUICKBOOKS_ONLINE: 'quickbooks_online',
  SLACK_INTEGRATION: 'slack_integration',
  GITHUB_ZOOM: 'github_zoom',
  MCP_SERVER: 'mcp_server',
  CUSTOM_PERMISSION_SETS: 'custom_permission_sets',
  TIMESHEET_LOCKING: 'timesheet_locking',
  AUDIT_LOG: 'audit_log',
  SCQL_SAVED_QUERIES: 'scql_saved_queries',
  CALCULATED_FIELDS: 'calculated_fields',
  OUTBOUND_WEBHOOKS: 'outbound_webhooks',
  AGENT_ACCOUNTS: 'agent_accounts',
  BENNY_RUNS: 'benny_runs',
  AGENT_LEDGER: 'agent_ledger',
  MULTI_ENTITY: 'multi_entity',
  MARKETING: 'marketing',
  SEQUENCES: 'sequences',
  LIVE_CHAT: 'live_chat',
  WEB_TRACKING: 'web_tracking',
  ENRICHMENT: 'enrichment',
  CALLING: 'calling',

  // Support
  EMAIL_SUPPORT: 'email_support',
  PRIORITY_SUPPORT: 'priority_support',
  PRIORITY_ONBOARDING: 'priority_onboarding',
} as const

export type FeatureKey = (typeof FEATURE_KEYS)[keyof typeof FEATURE_KEYS]

// Feature definitions for seeding
export const SUBSCRIPTION_FEATURES = [
  {
    key: FEATURE_KEYS.MAX_USERS,
    name: 'Max Users',
    description: 'Maximum number of team members',
    category: 'collaboration',
    valueType: FeatureValueType.NUMBER,
    defaultValue: 3,
  },
  {
    key: FEATURE_KEYS.MAX_PROJECTS,
    name: 'Max Projects',
    description: 'Maximum number of active projects',
    category: 'collaboration',
    valueType: FeatureValueType.NUMBER,
    defaultValue: 2,
  },
  {
    key: FEATURE_KEYS.STORAGE_GB,
    name: 'Storage GB',
    description: 'Storage space in gigabytes',
    category: 'storage',
    valueType: FeatureValueType.NUMBER,
    defaultValue: 1,
  },
  {
    key: FEATURE_KEYS.MARKETING_HOURLY_SENDS,
    name: 'Marketing Hourly Sends',
    description: 'Maximum marketing messages sent per organization per hour',
    category: 'marketing',
    valueType: FeatureValueType.NUMBER,
    defaultValue: 50,
  },
  {
    key: FEATURE_KEYS.MARKETING_DAILY_SENDS,
    name: 'Marketing Daily Sends',
    description: 'Maximum marketing messages sent per organization per day',
    category: 'marketing',
    valueType: FeatureValueType.NUMBER,
    defaultValue: 100,
  },
  {
    key: FEATURE_KEYS.MAX_CUSTOM_FIELDS,
    name: 'Max Custom Fields',
    description: 'Maximum number of custom fields per module',
    category: 'collaboration',
    valueType: FeatureValueType.NUMBER,
    defaultValue: 3,
  },
  {
    key: FEATURE_KEYS.MAX_TEMPLATES,
    name: 'Max Templates',
    description: 'Maximum number of project templates',
    category: 'collaboration',
    valueType: FeatureValueType.NUMBER,
    defaultValue: 1,
  },
  {
    key: FEATURE_KEYS.MAX_REPORTS,
    name: 'Max Reports',
    description: 'Maximum number of saved reports',
    category: 'collaboration',
    valueType: FeatureValueType.NUMBER,
    defaultValue: 0,
  },
  {
    key: FEATURE_KEYS.MAX_DASHBOARDS,
    name: 'Max Dashboards',
    description: 'Maximum number of dashboards',
    category: 'collaboration',
    valueType: FeatureValueType.NUMBER,
    defaultValue: 0,
  },
  {
    key: FEATURE_KEYS.MAX_FORMS,
    name: 'Max Forms',
    description: 'Maximum number of forms',
    category: 'collaboration',
    valueType: FeatureValueType.NUMBER,
    defaultValue: 2,
  },
  {
    key: FEATURE_KEYS.MAX_COLLECTIONS,
    name: 'Max Collections',
    description: 'Maximum number of custom collections',
    category: 'collaboration',
    valueType: FeatureValueType.NUMBER,
    defaultValue: 10,
  },
  {
    key: FEATURE_KEYS.MAX_DEALS,
    name: 'Max Deals',
    description: 'Maximum number of deals',
    category: 'sales',
    valueType: FeatureValueType.NUMBER,
    defaultValue: 50,
  },
  {
    key: FEATURE_KEYS.MAX_CONTACTS,
    name: 'Max Contacts',
    description: 'Maximum number of contacts',
    category: 'sales',
    valueType: FeatureValueType.NUMBER,
    defaultValue: 25000,
  },
  {
    key: FEATURE_KEYS.MAX_ENTITIES,
    name: 'Max Entities',
    description: 'Maximum number of entities (clients/companies)',
    category: 'sales',
    valueType: FeatureValueType.NUMBER,
    defaultValue: 50,
  },
  {
    key: FEATURE_KEYS.AI_CREDITS_MONTHLY,
    name: 'AI Credits Monthly',
    description: 'Monthly AI credits for AI-powered features',
    category: 'ai',
    valueType: FeatureValueType.NUMBER,
    defaultValue: 0,
  },
  {
    key: FEATURE_KEYS.PROJECTS,
    name: 'Projects',
    description: 'Access to project management features',
    category: 'collaboration',
    valueType: FeatureValueType.BOOLEAN,
    defaultValue: true,
  },
  {
    key: FEATURE_KEYS.TASKS_UNLIMITED,
    name: 'Tasks Unlimited',
    description: 'Unlimited tasks across all projects',
    category: 'collaboration',
    valueType: FeatureValueType.BOOLEAN,
    defaultValue: true,
  },
  {
    key: FEATURE_KEYS.TICKETS,
    name: 'Tickets',
    description: 'Help desk ticketing and request tracking',
    category: 'collaboration',
    valueType: FeatureValueType.BOOLEAN,
    defaultValue: true,
  },
  {
    key: FEATURE_KEYS.TIME_TRACKING,
    name: 'Time Tracking',
    description: 'Track time on tasks and projects',
    category: 'analytics',
    valueType: FeatureValueType.BOOLEAN,
    defaultValue: true,
  },
  {
    key: FEATURE_KEYS.DISCUSSIONS,
    name: 'Discussions',
    description: 'Team discussions and comments',
    category: 'collaboration',
    valueType: FeatureValueType.BOOLEAN,
    defaultValue: true,
  },
  {
    key: FEATURE_KEYS.FORMS,
    name: 'Forms',
    description: 'Build and publish forms to collect submissions',
    category: 'collaboration',
    valueType: FeatureValueType.BOOLEAN,
    defaultValue: false,
  },
  {
    key: FEATURE_KEYS.ENTITY_PORTAL,
    name: 'Entity Portal',
    description: 'Dedicated portal for client access',
    category: 'collaboration',
    valueType: FeatureValueType.BOOLEAN,
    defaultValue: false,
  },
  {
    key: FEATURE_KEYS.DEALS,
    name: 'Deals Management',
    description: 'Manage sales deals and pipeline',
    category: 'sales',
    valueType: FeatureValueType.BOOLEAN,
    defaultValue: false,
  },
  {
    key: FEATURE_KEYS.EVENTS,
    name: 'Events',
    description: 'Event management and scheduling',
    category: 'business',
    valueType: FeatureValueType.BOOLEAN,
    defaultValue: false,
  },
  {
    key: FEATURE_KEYS.DIRECTORY,
    name: 'Directory',
    description: 'Team and contact directory',
    category: 'business',
    valueType: FeatureValueType.BOOLEAN,
    defaultValue: false,
  },
  {
    key: FEATURE_KEYS.TEAM,
    name: 'Team',
    description: 'Team management, scheduling, and resource allocation',
    category: 'business',
    valueType: FeatureValueType.BOOLEAN,
    defaultValue: false,
  },
  {
    key: FEATURE_KEYS.INVENTORY,
    name: 'Inventory',
    description: 'Inventory management and tracking',
    category: 'business',
    valueType: FeatureValueType.BOOLEAN,
    defaultValue: false,
  },
  {
    key: FEATURE_KEYS.SUPPLIERS,
    name: 'Suppliers',
    description: 'Supplier and vendor management',
    category: 'business',
    valueType: FeatureValueType.BOOLEAN,
    defaultValue: false,
  },
  {
    key: FEATURE_KEYS.CONTRACTS,
    name: 'Contracts',
    description: 'Contract creation and tracking',
    category: 'business',
    valueType: FeatureValueType.BOOLEAN,
    defaultValue: false,
  },
  {
    key: FEATURE_KEYS.ADVANCED_REPORTS,
    name: 'Advanced Reports',
    description: 'Advanced analytics and reporting',
    category: 'analytics',
    valueType: FeatureValueType.BOOLEAN,
    defaultValue: false,
  },
  {
    key: FEATURE_KEYS.API_ACCESS,
    name: 'API Access',
    description: 'API access for integrations',
    category: 'integration',
    valueType: FeatureValueType.BOOLEAN,
    defaultValue: false,
  },
  {
    key: FEATURE_KEYS.WORKFLOWS,
    name: 'Workflows',
    description: 'Automated workflows and triggers',
    category: 'automation',
    valueType: FeatureValueType.BOOLEAN,
    defaultValue: false,
  },
  {
    key: FEATURE_KEYS.ANALYTICS,
    name: 'Analytics',
    description: 'Analytics dashboards and insights',
    category: 'analytics',
    valueType: FeatureValueType.BOOLEAN,
    defaultValue: false,
  },
  {
    key: FEATURE_KEYS.COLLECTIONS,
    name: 'Collections',
    description: 'Organize items into collections',
    category: 'organization',
    valueType: FeatureValueType.BOOLEAN,
    defaultValue: false,
  },
  {
    key: FEATURE_KEYS.INTEGRATIONS,
    name: 'Integrations',
    description:
      'Connect third-party services like Gmail, Outlook, and Google Calendar',
    category: 'integration',
    valueType: FeatureValueType.BOOLEAN,
    defaultValue: false,
  },
  {
    key: FEATURE_KEYS.DATA_EXPORT,
    name: 'Data Export',
    description: 'Export data to CSV, Excel, and other formats',
    category: 'integration',
    valueType: FeatureValueType.BOOLEAN,
    defaultValue: false,
  },
  {
    key: FEATURE_KEYS.REMOVE_WATERMARK,
    name: 'Remove Watermark',
    description: 'Remove Scrambl branding from invoices and forms',
    category: 'branding',
    valueType: FeatureValueType.BOOLEAN,
    defaultValue: false,
  },
  {
    key: FEATURE_KEYS.FORM_CAPTCHA,
    name: 'Form CAPTCHA',
    description: 'Enable reCAPTCHA spam protection on forms',
    category: 'security',
    valueType: FeatureValueType.BOOLEAN,
    defaultValue: false,
  },
  {
    key: FEATURE_KEYS.FORM_KIOSK_MODE,
    name: 'Form Kiosk Mode',
    description: 'Allow shared-device form submissions (bypasses CAPTCHA)',
    category: 'security',
    valueType: FeatureValueType.BOOLEAN,
    defaultValue: false,
  },
  {
    key: FEATURE_KEYS.EMAIL_SUPPORT,
    name: 'Email Support',
    description: 'Email support',
    category: 'support',
    valueType: FeatureValueType.BOOLEAN,
    defaultValue: false,
  },
  {
    key: FEATURE_KEYS.PRIORITY_SUPPORT,
    name: 'Priority Support',
    description: 'Priority customer support',
    category: 'support',
    valueType: FeatureValueType.BOOLEAN,
    defaultValue: false,
  },
  ...(
    [
      [FEATURE_KEYS.SPRINTS_CYCLES, 'Sprints and Cycles', 'collaboration'],
      [FEATURE_KEYS.FOCUS_MODE, 'Focus Mode', 'collaboration'],
      [FEATURE_KEYS.NOTES_DOCUMENTS, 'Notes and Documents', 'collaboration'],
      [FEATURE_KEYS.CALENDAR_PLANNER, 'Calendar and Planner', 'collaboration'],
      [
        FEATURE_KEYS.PERSONAL_CALENDAR_EMAIL_SYNC,
        'Personal Calendar and Email Sync',
        'integration',
      ],
      [FEATURE_KEYS.TIMESHEETS, 'Timesheets', 'analytics'],
      [FEATURE_KEYS.CONTACTS, 'Contacts', 'sales'],
      [FEATURE_KEYS.CUSTOM_FIELDS, 'Custom Fields', 'organization'],
      [FEATURE_KEYS.IMPORT_WIZARD, 'Import Wizard', 'organization'],
      [FEATURE_KEYS.DESKTOP_MOBILE_APPS, 'Desktop and Mobile Apps', 'platform'],
      [FEATURE_KEYS.CLIENTS, 'Clients', 'sales'],
      [
        FEATURE_KEYS.PORTAL_PERMISSION_TEMPLATES,
        'Portal Permission Templates',
        'collaboration',
      ],
      [FEATURE_KEYS.CLIENT_HEALTH, 'Client Health and Lifecycle', 'sales'],
      [FEATURE_KEYS.QUOTES, 'Quotes', 'sales'],
      [FEATURE_KEYS.PRODUCTS, 'Products', 'sales'],
      [FEATURE_KEYS.INVOICES, 'Invoicing', 'finance'],
      [FEATURE_KEYS.ONLINE_PAYMENTS, 'Online Invoice Payments', 'finance'],
      [FEATURE_KEYS.BILLING_SCHEDULES, 'Billing Schedules', 'finance'],
      [FEATURE_KEYS.CONTRACTOR_INVOICES, 'Contractor Invoices', 'finance'],
      [FEATURE_KEYS.EXPENSES, 'Expenses', 'finance'],
      [FEATURE_KEYS.BUDGETS, 'Budgets', 'finance'],
      [FEATURE_KEYS.RATE_CARDS, 'Rate Cards and Cost Rates', 'finance'],
      [FEATURE_KEYS.PROFITABILITY, 'Profitability', 'finance'],
      [FEATURE_KEYS.RESOURCE_PLANNING, 'Resource Planning', 'operations'],
      [FEATURE_KEYS.SCHEDULING, 'Scheduling', 'operations'],
      [FEATURE_KEYS.MEETING_RECORDER, 'Meeting Recorder', 'collaboration'],
      [FEATURE_KEYS.KNOWLEDGE_BASE, 'Knowledge Base', 'collaboration'],
      [FEATURE_KEYS.QUICKBOOKS_ONLINE, 'QuickBooks Online', 'integration'],
      [FEATURE_KEYS.SLACK_INTEGRATION, 'Slack', 'integration'],
      [FEATURE_KEYS.GITHUB_ZOOM, 'GitHub and Zoom', 'integration'],
      [FEATURE_KEYS.MCP_SERVER, 'MCP Server', 'integration'],
      [
        FEATURE_KEYS.CUSTOM_PERMISSION_SETS,
        'Custom Permission Sets',
        'security',
      ],
      [FEATURE_KEYS.TIMESHEET_LOCKING, 'Timesheet Locking', 'security'],
      [FEATURE_KEYS.AUDIT_LOG, 'Audit Log', 'security'],
      [FEATURE_KEYS.SCQL_SAVED_QUERIES, 'ScQL Saved Queries', 'analytics'],
      [FEATURE_KEYS.CALCULATED_FIELDS, 'Calculated Fields', 'organization'],
      [FEATURE_KEYS.OUTBOUND_WEBHOOKS, 'Outbound Webhooks', 'integration'],
      [FEATURE_KEYS.AGENT_ACCOUNTS, 'Agent Accounts', 'ai'],
      [FEATURE_KEYS.BENNY_RUNS, 'Hand Tasks to Benny', 'ai'],
      [FEATURE_KEYS.AGENT_LEDGER, 'Agent Labor Ledger', 'finance'],
      [FEATURE_KEYS.MULTI_ENTITY, 'Multi-entity Switching', 'organization'],
      [FEATURE_KEYS.MARKETING, 'Marketing Campaigns and Journeys', 'marketing'],
      [FEATURE_KEYS.SEQUENCES, 'Sequences', 'marketing'],
      [FEATURE_KEYS.LIVE_CHAT, 'Live Chat', 'marketing'],
      [FEATURE_KEYS.WEB_TRACKING, 'Web Tracking', 'marketing'],
      [FEATURE_KEYS.ENRICHMENT, 'Enrichment', 'marketing'],
      [FEATURE_KEYS.CALLING, 'Calling', 'marketing'],
      [FEATURE_KEYS.PRIORITY_ONBOARDING, 'Priority Onboarding', 'support'],
    ] as const
  ).map(([key, name, category]) => ({
    key,
    name,
    description: name,
    category,
    valueType: FeatureValueType.BOOLEAN,
    defaultValue: false,
  })),
] as const

// Subscription plan configuration interface
export interface SubscriptionPlanConfig {
  key: string
  name: string
  displayName: string
  description: string
  price: number // Monthly price
  annualPrice: number // Annual price
  pricePerUser: number // Per-user pricing
  minSeats: number // Minimum billable seats
  trialPeriodDays: number
  sortOrder: number
  stripeProductId: string
  stripePriceId: string // Monthly Stripe price ID
  stripeAnnualPriceId: string // Annual Stripe price ID
  isComingSoon?: boolean // Flag to indicate if plan is coming soon
  features: Record<FeatureKey, boolean | number>
}

// Helper function to get environment variable or return default
const getEnvVar = (key: string, defaultValue = ''): string =>
  typeof process === 'undefined'
    ? defaultValue
    : (process.env[key] ?? defaultValue)

const NEW_PLAN_FEATURE_DEFAULTS = {
  [FEATURE_KEYS.SPRINTS_CYCLES]: false,
  [FEATURE_KEYS.FOCUS_MODE]: false,
  [FEATURE_KEYS.NOTES_DOCUMENTS]: false,
  [FEATURE_KEYS.CALENDAR_PLANNER]: false,
  [FEATURE_KEYS.PERSONAL_CALENDAR_EMAIL_SYNC]: false,
  [FEATURE_KEYS.TIMESHEETS]: false,
  [FEATURE_KEYS.CONTACTS]: false,
  [FEATURE_KEYS.CUSTOM_FIELDS]: false,
  [FEATURE_KEYS.IMPORT_WIZARD]: false,
  [FEATURE_KEYS.DESKTOP_MOBILE_APPS]: false,
  [FEATURE_KEYS.CLIENTS]: false,
  [FEATURE_KEYS.PORTAL_PERMISSION_TEMPLATES]: false,
  [FEATURE_KEYS.CLIENT_HEALTH]: false,
  [FEATURE_KEYS.QUOTES]: false,
  [FEATURE_KEYS.PRODUCTS]: false,
  [FEATURE_KEYS.INVOICES]: false,
  [FEATURE_KEYS.ONLINE_PAYMENTS]: false,
  [FEATURE_KEYS.BILLING_SCHEDULES]: false,
  [FEATURE_KEYS.CONTRACTOR_INVOICES]: false,
  [FEATURE_KEYS.EXPENSES]: false,
  [FEATURE_KEYS.BUDGETS]: false,
  [FEATURE_KEYS.RATE_CARDS]: false,
  [FEATURE_KEYS.PROFITABILITY]: false,
  [FEATURE_KEYS.RESOURCE_PLANNING]: false,
  [FEATURE_KEYS.SCHEDULING]: false,
  [FEATURE_KEYS.MEETING_RECORDER]: false,
  [FEATURE_KEYS.KNOWLEDGE_BASE]: false,
  [FEATURE_KEYS.QUICKBOOKS_ONLINE]: false,
  [FEATURE_KEYS.SLACK_INTEGRATION]: false,
  [FEATURE_KEYS.GITHUB_ZOOM]: false,
  [FEATURE_KEYS.MCP_SERVER]: false,
  [FEATURE_KEYS.CUSTOM_PERMISSION_SETS]: false,
  [FEATURE_KEYS.TIMESHEET_LOCKING]: false,
  [FEATURE_KEYS.AUDIT_LOG]: false,
  [FEATURE_KEYS.SCQL_SAVED_QUERIES]: false,
  [FEATURE_KEYS.CALCULATED_FIELDS]: false,
  [FEATURE_KEYS.MAX_REPORTS]: 0,
  [FEATURE_KEYS.MAX_DASHBOARDS]: 0,
  [FEATURE_KEYS.OUTBOUND_WEBHOOKS]: false,
  [FEATURE_KEYS.AGENT_ACCOUNTS]: false,
  [FEATURE_KEYS.BENNY_RUNS]: false,
  [FEATURE_KEYS.AGENT_LEDGER]: false,
  [FEATURE_KEYS.MULTI_ENTITY]: false,
  [FEATURE_KEYS.MARKETING]: false,
  [FEATURE_KEYS.SEQUENCES]: false,
  [FEATURE_KEYS.LIVE_CHAT]: false,
  [FEATURE_KEYS.WEB_TRACKING]: false,
  [FEATURE_KEYS.ENRICHMENT]: false,
  [FEATURE_KEYS.CALLING]: false,
  [FEATURE_KEYS.PRIORITY_ONBOARDING]: false,
} as const

const CORE_STRUCTURE_FEATURES = {
  // Analytics is metered by report/dashboard count from here up rather than
  // hidden below Business. Attio's model: everyone sees reporting, volume
  // drives the upgrade.
  [FEATURE_KEYS.ANALYTICS]: true,
  [FEATURE_KEYS.SPRINTS_CYCLES]: true,
  [FEATURE_KEYS.FOCUS_MODE]: true,
  [FEATURE_KEYS.NOTES_DOCUMENTS]: true,
  [FEATURE_KEYS.CALENDAR_PLANNER]: true,
  [FEATURE_KEYS.PERSONAL_CALENDAR_EMAIL_SYNC]: true,
  [FEATURE_KEYS.TIMESHEETS]: true,
  [FEATURE_KEYS.CONTACTS]: true,
  [FEATURE_KEYS.CUSTOM_FIELDS]: true,
  [FEATURE_KEYS.IMPORT_WIZARD]: true,
  [FEATURE_KEYS.DESKTOP_MOBILE_APPS]: true,
  // Benny working tasks in the background is on every paid plan; Free keeps
  // Benny chat only. (Agent accounts for external agents start at Pro.)
  [FEATURE_KEYS.BENNY_RUNS]: true,
} as const

const PRO_STRUCTURE_FEATURES = {
  [FEATURE_KEYS.CLIENTS]: true,
  [FEATURE_KEYS.PORTAL_PERMISSION_TEMPLATES]: true,
  [FEATURE_KEYS.CLIENT_HEALTH]: true,
  [FEATURE_KEYS.QUOTES]: true,
  [FEATURE_KEYS.PRODUCTS]: true,
  [FEATURE_KEYS.INVOICES]: true,
  // Clients pay invoices online through the agency's own Stripe account.
  [FEATURE_KEYS.ONLINE_PAYMENTS]: true,
  [FEATURE_KEYS.BILLING_SCHEDULES]: true,
  [FEATURE_KEYS.CONTRACTOR_INVOICES]: true,
  [FEATURE_KEYS.EXPENSES]: true,
  [FEATURE_KEYS.BUDGETS]: true,
  [FEATURE_KEYS.RATE_CARDS]: true,
  [FEATURE_KEYS.PROFITABILITY]: true,
  [FEATURE_KEYS.RESOURCE_PLANNING]: true,
  [FEATURE_KEYS.SCHEDULING]: true,
  [FEATURE_KEYS.MEETING_RECORDER]: true,
  [FEATURE_KEYS.KNOWLEDGE_BASE]: true,
  [FEATURE_KEYS.CALLING]: true,
  [FEATURE_KEYS.QUICKBOOKS_ONLINE]: true,
  [FEATURE_KEYS.SLACK_INTEGRATION]: true,
  [FEATURE_KEYS.GITHUB_ZOOM]: true,
  // Product decision: MCP is included in Pro as well as Business.
  [FEATURE_KEYS.MCP_SERVER]: true,
  // Organization admins can create and manage access levels on Pro and above.
  [FEATURE_KEYS.CUSTOM_PERMISSION_SETS]: true,
  // The developer platform travels with MCP. Gating the REST API and webhooks a
  // tier above it left Pro able to point an AI agent at its data but unable to
  // run a script or receive a webhook, and put any Zapier/Make connector out of
  // reach of most customers.
  [FEATURE_KEYS.API_ACCESS]: true,
  [FEATURE_KEYS.OUTBOUND_WEBHOOKS]: true,
  // Agent accounts travel with MCP too: Pro can already connect an AI app, so
  // it can also give that agent its own identity, budget and review loop.
  [FEATURE_KEYS.AGENT_ACCOUNTS]: true,
  // Agent work in Profitability (human/agent split, leverage). Builds on
  // PROFITABILITY and AGENT_ACCOUNTS, both Pro and up.
  [FEATURE_KEYS.AGENT_LEDGER]: true,
  // Enrichment is what makes captured contacts useful rather than a bare email
  // address, so it has to reach the tier that buys client work.
  [FEATURE_KEYS.ENRICHMENT]: true,
} as const

const BUSINESS_STRUCTURE_FEATURES = {
  [FEATURE_KEYS.TIMESHEET_LOCKING]: true,
  [FEATURE_KEYS.AUDIT_LOG]: true,
  [FEATURE_KEYS.SCQL_SAVED_QUERIES]: true,
  [FEATURE_KEYS.CALCULATED_FIELDS]: true,
  [FEATURE_KEYS.MULTI_ENTITY]: true,
  [FEATURE_KEYS.MARKETING]: true,
  [FEATURE_KEYS.SEQUENCES]: true,
  [FEATURE_KEYS.LIVE_CHAT]: true,
  [FEATURE_KEYS.WEB_TRACKING]: true,
  [FEATURE_KEYS.CALLING]: true,
  [FEATURE_KEYS.PRIORITY_ONBOARDING]: true,
} as const

// Complete subscription plan configurations - Single source of truth
export const SUBSCRIPTION_PLAN_CONFIGS: SubscriptionPlanConfig[] = [
  {
    key: 'free',
    name: 'free',
    displayName: 'Free',
    description: 'Perfect for individuals',
    price: 0,
    annualPrice: 0,
    pricePerUser: 0, // $0 per user (free)
    minSeats: 1,
    trialPeriodDays: 0,
    sortOrder: 0,
    stripeProductId: getEnvVar('STRIPE_FREE_PRODUCT_ID', 'prod_SSMpS0MlwxtOi6'),
    stripePriceId: getEnvVar(
      'STRIPE_FREE_PRICE_ID',
      'price_1RXS6ER0phIDD6nqyhzekQva',
    ),
    stripeAnnualPriceId: getEnvVar(
      'STRIPE_FREE_ANNUAL_PRICE_ID',
      'price_1RXS6ER0phIDD6nqyhzekQva',
    ),
    features: {
      ...NEW_PLAN_FEATURE_DEFAULTS,
      // Limits
      [FEATURE_KEYS.MAX_USERS]: 10, // Allow up to 10 users (seats) on free plan
      [FEATURE_KEYS.MAX_PROJECTS]: 2,
      [FEATURE_KEYS.MAX_CUSTOM_FIELDS]: 10, // Limited to 10 custom fields per module
      [FEATURE_KEYS.MAX_TEMPLATES]: 1, // Limited to 1 project template
      [FEATURE_KEYS.MAX_FORMS]: 2, // Limited to 2 forms (public only)
      [FEATURE_KEYS.MAX_REPORTS]: 0,
      [FEATURE_KEYS.MAX_DASHBOARDS]: 0,
      [FEATURE_KEYS.MAX_COLLECTIONS]: 10, // Limited to 10 collections
      [FEATURE_KEYS.MAX_DEALS]: 50, // Limited to 50 deals
      [FEATURE_KEYS.MAX_CONTACTS]: 25000, // Limited to 25,000 contacts
      [FEATURE_KEYS.MAX_ENTITIES]: 50, // Limited to 50 entities
      [FEATURE_KEYS.STORAGE_GB]: 1,
      [FEATURE_KEYS.MARKETING_HOURLY_SENDS]: 50,
      [FEATURE_KEYS.MARKETING_DAILY_SENDS]: 100,

      // AI Features
      [FEATURE_KEYS.AI_CREDITS_MONTHLY]: 50,

      // Core Features
      [FEATURE_KEYS.PROJECTS]: true,
      [FEATURE_KEYS.TASKS_UNLIMITED]: true,
      [FEATURE_KEYS.TICKETS]: false, // Not in free tier
      [FEATURE_KEYS.TIME_TRACKING]: true,
      [FEATURE_KEYS.DISCUSSIONS]: true, // Enabled for all plans - core collaboration feature
      [FEATURE_KEYS.FORMS]: false, // Not in free tier

      // Entity Management
      [FEATURE_KEYS.ENTITY_PORTAL]: false,
      [FEATURE_KEYS.DEALS]: false, // Not in free tier

      // Business Features
      [FEATURE_KEYS.EVENTS]: false,
      [FEATURE_KEYS.DIRECTORY]: false,
      [FEATURE_KEYS.TEAM]: false, // Not in free tier
      [FEATURE_KEYS.INVENTORY]: true,
      [FEATURE_KEYS.SUPPLIERS]: false, // Not in free tier
      [FEATURE_KEYS.CONTRACTS]: false, // Not in free tier

      // Advanced Features
      [FEATURE_KEYS.ADVANCED_REPORTS]: false,
      [FEATURE_KEYS.API_ACCESS]: false,
      [FEATURE_KEYS.WORKFLOWS]: false, // Not in free tier
      [FEATURE_KEYS.ANALYTICS]: false, // Not in free tier
      [FEATURE_KEYS.COLLECTIONS]: false, // Not in free tier
      [FEATURE_KEYS.INTEGRATIONS]: false, // Not in free tier
      [FEATURE_KEYS.DATA_EXPORT]: false, // No CSV/Excel export in free tier
      [FEATURE_KEYS.REMOVE_WATERMARK]: false, // Shows "Powered by Scrambl" watermark
      [FEATURE_KEYS.FORM_CAPTCHA]: false, // No reCAPTCHA on free tier
      [FEATURE_KEYS.FORM_KIOSK_MODE]: false, // No kiosk mode on free tier

      // Support
      [FEATURE_KEYS.EMAIL_SUPPORT]: false,
      [FEATURE_KEYS.PRIORITY_SUPPORT]: false,
    },
  },
  {
    key: 'core',
    name: 'core',
    displayName: 'Core',
    description: 'The internal team layer for running your work',
    price: 15,
    annualPrice: 108,
    pricePerUser: 15,
    minSeats: 3,
    trialPeriodDays: 30,
    sortOrder: 1,
    stripeProductId: getEnvVar('STRIPE_CORE_PRODUCT_ID', 'prod_V3ybRA8WzUMHvn'),
    stripePriceId: getEnvVar(
      'STRIPE_CORE_PRICE_ID',
      'price_1U3qegJpDoPtvtpe6xN6v0sn',
    ),
    stripeAnnualPriceId: getEnvVar(
      'STRIPE_CORE_ANNUAL_PRICE_ID',
      'price_1U3qfBJpDoPtvtpeHqEs5oCs',
    ),
    features: {
      ...NEW_PLAN_FEATURE_DEFAULTS,
      ...CORE_STRUCTURE_FEATURES,
      [FEATURE_KEYS.MAX_USERS]: -1,
      [FEATURE_KEYS.MAX_PROJECTS]: -1,
      [FEATURE_KEYS.MAX_CUSTOM_FIELDS]: 10,
      [FEATURE_KEYS.MAX_TEMPLATES]: 3,
      [FEATURE_KEYS.MAX_FORMS]: 0,
      [FEATURE_KEYS.MAX_REPORTS]: 3,
      [FEATURE_KEYS.MAX_DASHBOARDS]: 1,
      [FEATURE_KEYS.MAX_COLLECTIONS]: 10,
      [FEATURE_KEYS.MAX_DEALS]: 0,
      [FEATURE_KEYS.MAX_CONTACTS]: -1,
      [FEATURE_KEYS.MAX_ENTITIES]: 0,
      [FEATURE_KEYS.STORAGE_GB]: 25,
      [FEATURE_KEYS.MARKETING_HOURLY_SENDS]: 0,
      [FEATURE_KEYS.MARKETING_DAILY_SENDS]: 0,
      [FEATURE_KEYS.AI_CREDITS_MONTHLY]: 100,
      [FEATURE_KEYS.PROJECTS]: true,
      [FEATURE_KEYS.TASKS_UNLIMITED]: true,
      [FEATURE_KEYS.TICKETS]: false,
      [FEATURE_KEYS.TIME_TRACKING]: true,
      [FEATURE_KEYS.DISCUSSIONS]: true,
      [FEATURE_KEYS.FORMS]: false,
      [FEATURE_KEYS.ENTITY_PORTAL]: false,
      [FEATURE_KEYS.DEALS]: false,
      [FEATURE_KEYS.EVENTS]: false,
      [FEATURE_KEYS.DIRECTORY]: true,
      [FEATURE_KEYS.TEAM]: true,
      [FEATURE_KEYS.INVENTORY]: false,
      [FEATURE_KEYS.SUPPLIERS]: false,
      [FEATURE_KEYS.CONTRACTS]: false,
      [FEATURE_KEYS.ADVANCED_REPORTS]: false,
      [FEATURE_KEYS.API_ACCESS]: false,
      [FEATURE_KEYS.WORKFLOWS]: false,
      [FEATURE_KEYS.ANALYTICS]: true, // Metered by MAX_REPORTS / MAX_DASHBOARDS
      [FEATURE_KEYS.COLLECTIONS]: true,
      [FEATURE_KEYS.INTEGRATIONS]: false,
      [FEATURE_KEYS.DATA_EXPORT]: false,
      [FEATURE_KEYS.REMOVE_WATERMARK]: false,
      [FEATURE_KEYS.FORM_CAPTCHA]: false,
      [FEATURE_KEYS.FORM_KIOSK_MODE]: false,
      [FEATURE_KEYS.EMAIL_SUPPORT]: true,
      [FEATURE_KEYS.PRIORITY_SUPPORT]: false,
    },
  },
  {
    key: 'pro',
    name: 'pro',
    displayName: 'Pro',
    description: 'Client work, billing, and live margin end to end',
    price: 39,
    annualPrice: 348,
    pricePerUser: 39,
    minSeats: 5,
    trialPeriodDays: 30,
    sortOrder: 2,
    stripeProductId: getEnvVar('STRIPE_PRO_PRODUCT_ID', 'prod_SUd3mNqdu865Yp'),
    stripePriceId: getEnvVar(
      'STRIPE_PRO_PRICE_ID',
      'price_1U4O2vJpDoPtvtpeRQw6RRoE',
    ),
    stripeAnnualPriceId: getEnvVar(
      'STRIPE_PRO_ANNUAL_PRICE_ID',
      'price_1U4O3SJpDoPtvtpeoIeq1u3z',
    ),
    features: {
      ...NEW_PLAN_FEATURE_DEFAULTS,
      ...CORE_STRUCTURE_FEATURES,
      ...PRO_STRUCTURE_FEATURES,
      // Limits
      [FEATURE_KEYS.MAX_USERS]: -1,
      [FEATURE_KEYS.MAX_PROJECTS]: -1, // Unlimited projects
      [FEATURE_KEYS.MAX_CUSTOM_FIELDS]: -1, // Unlimited custom fields
      [FEATURE_KEYS.MAX_TEMPLATES]: -1, // Unlimited templates
      [FEATURE_KEYS.MAX_FORMS]: -1, // Unlimited forms
      [FEATURE_KEYS.MAX_REPORTS]: 15,
      [FEATURE_KEYS.MAX_DASHBOARDS]: 5,
      [FEATURE_KEYS.MAX_COLLECTIONS]: -1, // Unlimited collections
      [FEATURE_KEYS.MAX_DEALS]: -1, // Unlimited deals
      [FEATURE_KEYS.MAX_CONTACTS]: -1, // Unlimited contacts
      [FEATURE_KEYS.MAX_ENTITIES]: -1, // Unlimited entities
      [FEATURE_KEYS.STORAGE_GB]: 250,
      [FEATURE_KEYS.MARKETING_HOURLY_SENDS]: 0,
      [FEATURE_KEYS.MARKETING_DAILY_SENDS]: 0,

      // AI Features
      [FEATURE_KEYS.AI_CREDITS_MONTHLY]: 400,

      // Core Features
      [FEATURE_KEYS.PROJECTS]: true,
      [FEATURE_KEYS.TASKS_UNLIMITED]: true,
      [FEATURE_KEYS.TICKETS]: true,
      [FEATURE_KEYS.TIME_TRACKING]: true,
      [FEATURE_KEYS.DISCUSSIONS]: true,
      [FEATURE_KEYS.FORMS]: true, // Included in pro

      // Entity Management
      [FEATURE_KEYS.ENTITY_PORTAL]: true,
      [FEATURE_KEYS.DEALS]: true,

      // Business Features
      [FEATURE_KEYS.EVENTS]: true,
      [FEATURE_KEYS.DIRECTORY]: true,
      [FEATURE_KEYS.TEAM]: true, // Included in pro
      [FEATURE_KEYS.INVENTORY]: false,
      [FEATURE_KEYS.SUPPLIERS]: true,
      [FEATURE_KEYS.CONTRACTS]: true, // Included in pro

      // Advanced Features
      [FEATURE_KEYS.ADVANCED_REPORTS]: false,
      [FEATURE_KEYS.API_ACCESS]: true, // Included in pro (see PRO_STRUCTURE_FEATURES)
      [FEATURE_KEYS.WORKFLOWS]: true, // Included in pro
      [FEATURE_KEYS.ANALYTICS]: true, // Metered by MAX_REPORTS / MAX_DASHBOARDS
      [FEATURE_KEYS.COLLECTIONS]: true, // Included in pro
      [FEATURE_KEYS.INTEGRATIONS]: true, // Included in pro
      [FEATURE_KEYS.DATA_EXPORT]: true, // CSV/Excel export included
      [FEATURE_KEYS.REMOVE_WATERMARK]: true, // No watermark on invoices/forms
      [FEATURE_KEYS.FORM_CAPTCHA]: true, // reCAPTCHA enabled for pro
      [FEATURE_KEYS.FORM_KIOSK_MODE]: true, // Kiosk mode enabled for pro

      // Support
      [FEATURE_KEYS.EMAIL_SUPPORT]: true,
      [FEATURE_KEYS.PRIORITY_SUPPORT]: false,
    },
  },
  {
    key: 'business',
    name: 'business',
    displayName: 'Business',
    description: 'Scale, control, automation, and the agent layer',
    price: 59,
    annualPrice: 588,
    pricePerUser: 59,
    minSeats: 10,
    trialPeriodDays: 30,
    sortOrder: 3,
    stripeProductId: getEnvVar(
      'STRIPE_BUSINESS_PRODUCT_ID',
      'prod_SUd3nTeLfSEpvP',
    ),
    stripePriceId: getEnvVar(
      'STRIPE_BUSINESS_PRICE_ID',
      'price_1U4O3nJpDoPtvtpeB20JqdSk',
    ),
    stripeAnnualPriceId: getEnvVar(
      'STRIPE_BUSINESS_ANNUAL_PRICE_ID',
      'price_1U4O3yJpDoPtvtpeDnhepslI',
    ),
    features: {
      ...NEW_PLAN_FEATURE_DEFAULTS,
      ...CORE_STRUCTURE_FEATURES,
      ...PRO_STRUCTURE_FEATURES,
      ...BUSINESS_STRUCTURE_FEATURES,
      // Limits
      [FEATURE_KEYS.MAX_USERS]: -1, // Unlimited
      [FEATURE_KEYS.MAX_PROJECTS]: -1, // Unlimited
      [FEATURE_KEYS.MAX_CUSTOM_FIELDS]: -1, // Unlimited custom fields
      [FEATURE_KEYS.MAX_TEMPLATES]: -1, // Unlimited templates
      [FEATURE_KEYS.MAX_FORMS]: -1, // Unlimited forms
      [FEATURE_KEYS.MAX_REPORTS]: -1,
      [FEATURE_KEYS.MAX_DASHBOARDS]: -1,
      [FEATURE_KEYS.MAX_COLLECTIONS]: -1, // Unlimited collections
      [FEATURE_KEYS.MAX_DEALS]: -1, // Unlimited deals
      [FEATURE_KEYS.MAX_CONTACTS]: -1, // Unlimited contacts
      [FEATURE_KEYS.MAX_ENTITIES]: -1, // Unlimited entities
      [FEATURE_KEYS.STORAGE_GB]: 500,
      [FEATURE_KEYS.MARKETING_HOURLY_SENDS]: 5000,
      [FEATURE_KEYS.MARKETING_DAILY_SENDS]: 50000,

      // AI Features
      [FEATURE_KEYS.AI_CREDITS_MONTHLY]: 1000,

      // Core Features
      [FEATURE_KEYS.PROJECTS]: true,
      [FEATURE_KEYS.TASKS_UNLIMITED]: true,
      [FEATURE_KEYS.TICKETS]: true,
      [FEATURE_KEYS.TIME_TRACKING]: true,
      [FEATURE_KEYS.DISCUSSIONS]: true,
      [FEATURE_KEYS.FORMS]: true, // Included in business

      // Entity Management
      [FEATURE_KEYS.ENTITY_PORTAL]: true,
      [FEATURE_KEYS.DEALS]: true,

      // Business Features
      [FEATURE_KEYS.EVENTS]: true,
      [FEATURE_KEYS.DIRECTORY]: true,
      [FEATURE_KEYS.TEAM]: true, // Included in business
      [FEATURE_KEYS.INVENTORY]: false,
      [FEATURE_KEYS.SUPPLIERS]: true,
      [FEATURE_KEYS.CONTRACTS]: true, // Included in business

      // Advanced Features
      [FEATURE_KEYS.ADVANCED_REPORTS]: true,
      [FEATURE_KEYS.API_ACCESS]: true,
      [FEATURE_KEYS.WORKFLOWS]: true, // Included in business
      [FEATURE_KEYS.ANALYTICS]: true, // Included in business
      [FEATURE_KEYS.COLLECTIONS]: true, // Included in business
      [FEATURE_KEYS.INTEGRATIONS]: true, // Included in business
      [FEATURE_KEYS.DATA_EXPORT]: true, // CSV/Excel export included
      [FEATURE_KEYS.REMOVE_WATERMARK]: true, // No watermark on invoices/forms
      [FEATURE_KEYS.FORM_CAPTCHA]: true, // reCAPTCHA enabled for business
      [FEATURE_KEYS.FORM_KIOSK_MODE]: true, // Kiosk mode enabled for business

      // Support
      [FEATURE_KEYS.EMAIL_SUPPORT]: true,
      [FEATURE_KEYS.PRIORITY_SUPPORT]: true,
    },
  },
]

const UPGRADE_PLAN_KEYS = ['core', 'pro', 'business'] as const

/**
 * Returns the first paid plan that includes a boolean subscription feature.
 * UI upgrade labels use this instead of assuming every locked module is Pro.
 */
export function getRequiredSubscriptionPlan(
  featureKey: string,
): SubscriptionPlanConfig | undefined {
  return UPGRADE_PLAN_KEYS.map(key =>
    SUBSCRIPTION_PLAN_CONFIGS.find(plan => plan.key === key),
  ).find(plan => plan?.features[featureKey as FeatureKey] === true)
}

export interface PricingFeatureGroup {
  label: string
  features: readonly string[]
}

/**
 * Curated launch-page copy. This intentionally omits coming-soon features and
 * modules that are not included in the launch plan structure.
 */
export const PRICING_FEATURE_GROUPS_BY_PLAN: Record<
  'core' | 'pro' | 'business',
  readonly PricingFeatureGroup[]
> = {
  core: [
    {
      label: 'Internal work',
      features: [
        'Unlimited projects with 3 project templates',
        'Unlimited tasks — list, board, table, deck and timeline',
        'Sprints and cycles',
        'My Work, Today and Updates',
        'Focus mode',
        'Notes and documents',
        'Discussions',
        'Calendar and planner',
        'Google and Outlook calendar/email sync',
        'Time tracking and timesheets',
        'Unlimited contacts',
        'Teams and org chart',
        '10 custom collections',
        '10 custom fields per module',
        'Import wizard',
        'Desktop and mobile apps',
      ],
    },
    {
      label: 'Storage & AI',
      features: ['25 GB storage', '100 Benny AI credits/user/month'],
    },
    { label: 'Support', features: ['Email support'] },
  ],
  pro: [
    {
      label: 'Client work & finance',
      features: [
        'Unlimited clients',
        'Client portal and permission templates',
        'Contacts, deals, client health and lifecycle',
        'Quotes and products',
        'Contracts and retainers',
        'Invoicing, billing schedules and authorizations',
        'Contractor invoices and expenses',
        'Budgets, rate cards and cost rates',
        'Profitability — live margin, P&L and forecasts',
        'Resource planning',
      ],
    },
    {
      label: 'Operations & automation',
      features: [
        'Forms',
        'Events',
        'Vendors and purchase orders',
        'Communications',
        'Custom access levels and permission sets',
        'Workflows and automations',
        'Scheduling',
        'Meeting recorder',
        'Knowledge base',
        'Data export and no watermark',
      ],
    },
    {
      label: 'Integrations',
      features: [
        'QuickBooks Online',
        'Slack',
        'GitHub and Zoom',
        'MCP server',
        'Agent accounts and agent work in Profitability',
      ],
    },
    {
      label: 'Storage & AI',
      features: ['250 GB storage', '400 Benny AI credits/user/month'],
    },
  ],
  business: [
    {
      label: 'Scale & control',
      features: [
        'Timesheet locking',
        'Audit log',
        'Advanced reporting and alerts',
        'ScQL saved queries',
        'Calculated fields',
        'API access and API keys',
        'Outbound webhooks',
        'Multi-entity switching',
        'Priority onboarding and support',
      ],
    },
    {
      label: 'Storage & AI',
      features: ['500 GB storage', '1,000 Benny AI credits/user/month'],
    },
  ],
}

// Legacy export for backward compatibility
export const DEFAULT_SUBSCRIPTION_PLANS = SUBSCRIPTION_PLAN_CONFIGS.map(
  config => ({
    key: config.key,
    displayName: config.displayName,
    description: config.description,
    monthlyPrice: config.price,
    annualPrice: config.annualPrice,
    pricePerUser: config.pricePerUser,
    minSeats: config.minSeats,
    trialDays: config.trialPeriodDays,
    sortOrder: config.sortOrder,
    features: config.features,
  }),
)
